import { api } from 'src/services';
import { getAPiVersion } from '.';
import { AxiosPromise } from 'axios';
import { Appointment } from 'src/models';

export type ChatCompletionStatus =
  | 'InProgress'
  | 'Completed'
  | 'New'
  | 'Summary InProgress';

/**
 * Validate token
 */
export type ValidateTokenApiPayload = {
  token: string;
};

export type ValidateTokenApiResponse = {
  completionStatus: ChatCompletionStatus;
  valid: boolean;
  expired: boolean;
  patientLogoutStatus: boolean;
  patientPreferredLanguage: string;
};

export const validateTokenApi = ({
  token,
}: ValidateTokenApiPayload): AxiosPromise<ValidateTokenApiResponse> => {
  const apiVersion = getAPiVersion();

  if (apiVersion === 'v3') {
    return api
      .post<{
        chat_completion_status: ChatCompletionStatus;
        valid: boolean;
        is_expired: boolean;
        patient_logout_status: boolean;
        patient_preferred_language: string;
      }>('/v3/validate', {
        access_token: token,
      })
      .then(
        ({
          data: {
            chat_completion_status,
            is_expired,
            patient_logout_status,
            patient_preferred_language,
            ...restData
          },
          ...rest
        }) => ({
          ...rest,
          data: {
            ...restData,
            completionStatus: chat_completion_status,
            expired: is_expired,
            patientLogoutStatus: patient_logout_status,
            patientPreferredLanguage: patient_preferred_language,
          },
        })
      );
  }

  return api
    .post<{
      chat_completion_status: ChatCompletionStatus;
      chat_link_valid: boolean;
      isExpired: boolean;
      patient_logout_status: boolean;
      patient_preferred_language: string;
      settings: unknown;
    }>('/validateEmailLink', {
      access_token: token,
    })
    .then(
      ({
        data: {
          chat_completion_status,
          chat_link_valid,
          isExpired,
          patient_logout_status,
          patient_preferred_language,
          ...restData
        },
        ...rest
      }) => ({
        ...rest,
        data: {
          ...restData,
          completionStatus: chat_completion_status,
          valid: chat_link_valid,
          expired: isExpired,
          patientLogoutStatus: patient_logout_status,
          patientPreferredLanguage: patient_preferred_language,
        },
      })
    );
};
/**
 * Validate logged in user
 */

export type ValidateLoggedInUserApiPayload = {
  chatToken: string;
};

export const validateLoggedInUserApi = ({
  chatToken,
}: ValidateLoggedInUserApiPayload): AxiosPromise<Appointment> => {
  const apiVersion = getAPiVersion();

  if (apiVersion === 'v3') {
    return api.post('/v3/login/practitioner', {
      access_token: chatToken,
    });
  }

  return api.post('/validateLoggedInUserData', {
    access_token: chatToken,
  });
};

/**
 * Generate OTP
 */
export type GenerateOtpApiPayload = {
  chatToken: string;
};

export type GenerateOtpApiResponse = {
  message: string;
};

export const generateOtpApi = ({
  chatToken,
}: GenerateOtpApiPayload): AxiosPromise<GenerateOtpApiResponse> => {
  const apiVersion = getAPiVersion();

  if (apiVersion === 'v3') {
    return api.post('/v3/otp/generate', {});
  }

  return api.post('/generate_otp', {
    appointment_access_token: chatToken,
  });
};

/**
 * Validate anonymous user
 */
export type ValidateAnonymousUserApiPayload = {
  chatToken: string;
  preferredLanguage?: string;
  otp?: string;
  dateOfBirth?: string;
};

export type ValidateAnonymousUserApiResponse = Appointment & {
  user_access_token: string;
};

export const validateAnonymousUserApi = ({
  chatToken,
  preferredLanguage,
  otp,
  dateOfBirth,
}: ValidateAnonymousUserApiPayload): AxiosPromise<ValidateAnonymousUserApiResponse> => {
  const apiVersion = getAPiVersion();

  const commonPayload = {
    access_token: chatToken,
    preferred_language: preferredLanguage,
    otp,
  };

  if (apiVersion === 'v3') {
    return api.post('/v3/login/patient', {
      ...commonPayload,
      date_of_birth: dateOfBirth,
    });
  }

  return api
    .post('/validateUserData', {
      ...commonPayload,
      dateOfBirth: dateOfBirth,
    })
    .then(({ data, ...rest }) => ({
      ...rest,
      data: {
        ...data,
        patient_first_name: data.patient_firstName,
        patient_last_name: data.patient_lastName,
        practitioner_first_name: data.practitioner_firstName,
        practitioner_last_name: data.practitioner_LastName,
        condition_type: data.is_Generic_Condition,
      },
    }));
};

/**
 * Get appointment conditions
 */
export type GetAppointmentConditionsApiPayload = {
  appointmentId: number | string;
};

export type GetAppointmentConditionsApiResponse = {
  condition_details: Array<{
    condition_id: number;
    condition_name: string;
  }>;
};

export const getAppointmentConditionsApi = ({
  appointmentId,
}: GetAppointmentConditionsApiPayload): AxiosPromise<GetAppointmentConditionsApiResponse> => {
  const apiVersion = getAPiVersion();

  if (apiVersion === 'v3') {
    return api.get('/v3/condition');
  }

  return api.post('/conditions', {
    appointment_id: appointmentId,
  });
};

/**
 * Update appointment details
 */
export type UpdateAppointmentDetailsApiPayload = {
  appointmentId: number | string;
  conditionId: number | string;
};

export const updateAppointmentDetailsApi = ({
  appointmentId,
  conditionId,
}: UpdateAppointmentDetailsApiPayload): AxiosPromise<{
  date_of_birth: string;
}> => {
  const apiVersion = getAPiVersion();

  if (apiVersion === 'v3') {
    return api.put('/v3/chat/condition', {
      appointment_id: appointmentId,
      condition_id: conditionId,
    });
  }

  return api
    .post('/chat/condition_id', {
      appointment_id: appointmentId,
      condition_id: conditionId,
    })
    .then(({ data, ...rest }) => ({
      ...rest,
      data: {
        ...data,
        date_of_birth: data.appointment_data.date_of_birth,
      },
    }));
};

/**
 * Logout
 */
type LogoutApiPayload = {
  chatToken: string;
};

export const logoutApi = ({
  chatToken,
}: LogoutApiPayload): AxiosPromise<unknown> => {
  const apiVersion = getAPiVersion();

  if (apiVersion === 'v3') {
    return api.post('/v3/logout');
  }

  return api.post('/logout', { chat_token: chatToken });
};
